import React, { useState } from 'react';
import {
  BubbleFormActions,
  BubbleFormFooter,
  BubbleFormHeader,
  FormPageComponentHelpfulProps,
} from '@go-client/components/BubbleUI/BubbleForm';
import ReservationFormik, {
  ReservationFormikForm,
} from '@go-client/components/Formik/ReservationFormik';
import { FormikProps } from 'formik';
import { FormGenericContainer } from '@go-client/components/FormGenericContainer';
import { SimpleUser } from '@go-shared/types/user';
import { ReservationFormMode } from '../ReservationPath';
import { Reservation } from '@go-shared/types/reservation';
import BubbleButton from '@go-client/components/BubbleUI/BubbleButton';

import { ReservableEventTemplateSnapshot } from '@go-shared/types/reservable';
import { formatCentsToUSD } from '@go-shared/lib/utils';
import {
  getEnhancedReservationStatus,
  getEnhancedStatusVariant,
  getReservationRevenue,
} from '@go-shared/lib/reserve';
import { canManageReservation } from '@go-shared/lib/permissions';
import { useUserInfo } from '@go-client/context/UserInfoContext';
import { usePublicServerInfo } from '@go-client/context/PublicServerInfoContext';
import { calculateReservationFormikPricing } from '@go-client/lib/reserve';
import { BubbleBadge } from '@go-client/components/BubbleUI';
import { camelToSentenceCase } from '@go-shared/lib/string';
import { ReservationActions } from '@go-client/pages/Reservables/ReserveTransactions';

interface EnterReservationPagePropsBase {
  formik: FormikProps<ReservationFormikForm>;
  mode: ReservationFormMode;
  users: SimpleUser[];
  selectedEventSnapshot: ReservableEventTemplateSnapshot;
  isBusy: boolean;
  setIsBusy: (isBusy: boolean) => void;
  onSubmitReservationForm: () => void;
  onRefreshReservation?: (reservation: Reservation) => void;
  onNavigateToReservation?: (reservation: Reservation) => void;
}

type EnterReservationPageProps = EnterReservationPagePropsBase & FormPageComponentHelpfulProps;

export default function EnterReservationPage(props: EnterReservationPageProps) {
  const { user } = useUserInfo();
  const { serverDate } = usePublicServerInfo();

  const canEdit = !props.mode.edit || canManageReservation(user, props.mode.edit);
  const disabled = !canEdit || !props.formik.dirty || !props.formik.isValid || props.isBusy;
  const [changingStatus, setChangingStatus] = useState(false);
  const [showDelete, setShowDelete] = useState<{ [key: string]: boolean }>({});

  return (
    <div className="eaf-2">
      <FormGenericContainer
        footer={() => (
          <>
            <BubbleFormFooter
              className="text-slate-dark mr-auto"
              items={[
                { label: 'Event Selected', value: props.selectedEventSnapshot.title },
                {
                  label: 'Reservation Selected',
                  value: props.mode.edit
                    ? `${props.mode.edit.reservationCode}${
                        props.mode.edit.cartCode ? ` (${props.mode.edit.cartCode})` : ''
                      }`
                    : undefined,
                },
                {
                  label: 'Payment Summary',
                  value: !props.mode.edit
                    ? formatCentsToUSD(
                        calculateReservationFormikPricing(
                          props.formik.values,
                          props.selectedEventSnapshot
                        ).finalTotal
                      )
                    : undefined,
                },
                {
                  label: 'Total Revenue',
                  value: props.mode.edit
                    ? formatCentsToUSD(getReservationRevenue(props.mode.edit))
                    : undefined,
                },
                {
                  label: 'Created By',
                  value: props.mode.edit?.metadata?.createdByName,
                },
                {
                  label: 'Last Updated By',
                  value: props.mode.edit?.metadata?.lastUpdatedByName,
                },
              ]}
            />
            <div className="flex items-center">
              {props.mode.edit && (
                <BubbleButton
                  variant="slate"
                  inverse
                  disabled={!props.formik.dirty}
                  onClick={() => props.formik.resetForm()}
                >
                  Reset
                </BubbleButton>
              )}
              <BubbleFormActions
                isBusy={props.isBusy}
                forwardTitle={props.forwardTitle || 'Finish: Save Changes'}
                onGoForward={props.onSubmitReservationForm}
                onGoBack={props.onGoBack}
                forwardDisabled={disabled}
              />
            </div>
          </>
        )}
      >
        <BubbleFormHeader
          title={props.title || 'Manage Reservation'}
          appendToTitle={
            props.mode.edit
              ? () => (
                  <>
                    <BubbleBadge
                      variant={getEnhancedStatusVariant(
                        props.mode.edit!,
                        serverDate._dangerousDate
                      )}
                      className="ml-2"
                    >
                      {camelToSentenceCase(
                        getEnhancedReservationStatus(props.mode.edit!, serverDate._dangerousDate)
                      )}
                    </BubbleBadge>
                    {props.mode.edit?.spotReleased && (
                      <BubbleBadge variant="dirt" className="ml-1">
                        Released
                      </BubbleBadge>
                    )}
                  </>
                )
              : undefined
          }
          subtitle={props.subtitle || 'Enter Reservation Details'}
          pageIndex={props.pageIndex}
          onClose={props.onClose}
          appendToCloseButton={() => (
            <>
              {props.mode.edit && (
                <ReservationActions
                  data={props.mode.edit}
                  changingStatus={changingStatus}
                  setChangingStatus={setChangingStatus}
                  showDelete={showDelete}
                  setShowDelete={setShowDelete}
                  fetchData={() => Promise.resolve(props.onRefreshReservation?.(props.mode.edit!))}
                  iconSize="xl"
                />
              )}
              <BubbleButton
                variant="slate"
                className="p-2-i mr-1"
                inverse
                onClick={() =>
                  props.mode.edit ? props.onRefreshReservation?.(props.mode.edit) : null
                }
                icon="rotate"
                iconSize="xl"
              />
            </>
          )}
        />
        <ReservationFormik
          type={!canEdit ? 'view' : props.mode.type}
          users={props.users}
          reservation={props.mode.edit}
          selectedEventSnapshot={props.selectedEventSnapshot}
          extManagedFormik={props.formik}
          subnavClassName="-mx-8"
          actionsAppearance="drawer-float"
          onRefreshReservation={props.onRefreshReservation}
          onNavigateToReservation={props.onNavigateToReservation}
          defaultView={
            props.mode.type === 'edit' && props.mode.goToPayments ? 'payments' : 'details'
          }
        />
      </FormGenericContainer>
    </div>
  );
}
